import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, PageTitle, GameContainer, Button, FlexContainer } from '../../styles/StyledComponents';
import { useGameContext } from '../../context/GameContext';

// Styled components for the game
const GameArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
  gap: 2rem;
`;

const ComparisonArea = styled.div`
  width: 100%;
  padding: 2rem;
  background-color: #fff8dc;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ComparisonZone = styled.div<{ itemCount?: number }>`
  width: 150px;
  height: 150px;
  border: 3px solid var(--primary-color);
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  font-size: ${props => {
    const count = props.itemCount || 1;
    if (count <= 2) return '4rem';
    if (count <= 4) return '2.5rem';
    if (count <= 6) return '2rem';
    if (count <= 8) return '1.5rem';
    return '1rem';
  }};
  box-shadow: var(--box-shadow);
  margin: 0.5rem;
  flex-wrap: wrap;
  gap: 0.2rem;
  padding: 0.5rem;
`;

const AnswerArea = styled.div`
  width: 100%;
  padding: 1rem;
  background-color: #f0fff0;
  border-radius: var(--border-radius);
  text-align: center;
`;

const AnswerButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const AnswerButton = styled(Button)<{ isSelected?: boolean; isCorrect?: boolean }>`
  background-color: ${props => 
    props.isSelected 
      ? (props.isCorrect ? 'var(--success-color)' : 'var(--error-color)')
      : 'var(--secondary-color)'
  };
  color: ${props => props.isSelected ? 'white' : 'var(--text-color)'};
  font-size: 1.2rem;
  padding: 1rem;
`;

const ScoreDisplay = styled.div`
  font-size: 1.2rem;
  text-align: right;
  margin-bottom: 1rem;

  span {
    font-weight: bold;
    color: var(--primary-color);
  }
`;

const InstructionText = styled.p`
  font-size: 1.3rem;
  text-align: center;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
`;

const FeedbackMessage = styled.div<{ isCorrect: boolean }>`
  font-size: 1.5rem;
  text-align: center;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: var(--border-radius);
  background-color: ${props => props.isCorrect ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'};
  color: ${props => props.isCorrect ? 'var(--success-color)' : 'var(--error-color)'};
`;

const BalanceScale = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;



// Game types and interfaces
type GameMode = 'scale' | 'count' | 'size';
type ComparisonResult = 'more' | 'less' | 'equal';

interface GameItem {
  id: string;
  emoji: string;
  value: number;
  type: 'weight' | 'count' | 'size';
}

interface Challenge {
  mode: GameMode;
  leftItem: GameItem;
  rightItem: GameItem;
  correctAnswer: ComparisonResult;
  instruction: string;
}

const ComparisonChampions: React.FC = () => {
  const [challenge, setChallenge] = useState<Challenge>(generateChallenge());
  const [selectedAnswer, setSelectedAnswer] = useState<ComparisonResult | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState<number>(0);
  const { updateProgress } = useGameContext();

  function generateChallenge(): Challenge {
    const modes: GameMode[] = ['scale', 'count', 'size'];
    const mode = modes[Math.floor(Math.random() * modes.length)];

    switch (mode) {
      case 'scale':
        return generateScaleChallenge();
      case 'count':
        return generateCountChallenge();
      case 'size':
        return generateSizeChallenge();
      default:
        return generateScaleChallenge();
    }
  }

  function generateScaleChallenge(): Challenge {
    const weightItems: GameItem[] = [
      // Very light items (value 1) - things that float or are mostly air
      { id: 'feather', emoji: '🪶', value: 1, type: 'weight' },
      { id: 'balloon', emoji: '🎈', value: 1, type: 'weight' },
      { id: 'leaf', emoji: '🍃', value: 1, type: 'weight' },
      { id: 'paper', emoji: '📄', value: 1, type: 'weight' },
      
      // Light items (value 2) - small fruits and light objects
      { id: 'apple', emoji: '🍎', value: 2, type: 'weight' },
      { id: 'orange', emoji: '🍊', value: 2, type: 'weight' },
      { id: 'banana', emoji: '🍌', value: 2, type: 'weight' },
      
      // Medium-light items (value 3) - bigger but hollow/air-filled
      { id: 'ball', emoji: '⚽', value: 3, type: 'weight' },
      { id: 'basketball', emoji: '🏀', value: 3, type: 'weight' },
      
      // Medium items (value 5) - solid everyday objects
      { id: 'book', emoji: '📚', value: 5, type: 'weight' },
      { id: 'shoe', emoji: '👟', value: 5, type: 'weight' },
      { id: 'bottle', emoji: '🍼', value: 5, type: 'weight' },
      { id: 'phone', emoji: '📱', value: 5, type: 'weight' },
      
      // Heavy items (value 7) - dense/solid objects
      { id: 'rock', emoji: '🪨', value: 7, type: 'weight' },
      { id: 'brick', emoji: '🧱', value: 7, type: 'weight' },
      { id: 'hammer', emoji: '🔨', value: 7, type: 'weight' },
      { id: 'laptop', emoji: '💻', value: 7, type: 'weight' },
      
      // Very heavy items (value 10) - obviously massive things
      { id: 'elephant', emoji: '🐘', value: 10, type: 'weight' },
      { id: 'car', emoji: '🚗', value: 10, type: 'weight' },
      { id: 'piano', emoji: '🎹', value: 10, type: 'weight' },
      { id: 'refrigerator', emoji: '🧊', value: 10, type: 'weight' },
    ];

    const leftItem = weightItems[Math.floor(Math.random() * weightItems.length)];
    const rightItem = weightItems[Math.floor(Math.random() * weightItems.length)];

    let correctAnswer: ComparisonResult = 'equal';
    if (leftItem.value > rightItem.value) correctAnswer = 'more';
    if (leftItem.value < rightItem.value) correctAnswer = 'less';

    return {
      mode: 'scale',
      leftItem,
      rightItem,
      correctAnswer,
      instruction: `Which item weighs more? Compare the ${leftItem.id} and the ${rightItem.id}!`
    };
  }

  function generateCountChallenge(): Challenge {
    const leftCount = Math.floor(Math.random() * 10) + 1;
    const rightCount = Math.floor(Math.random() * 10) + 1;

    // Different counting objects for variety
    const countingObjects = ['🔵', '🔴', '⭐', '🍎', '🍊', '🎈', '🏀', '⚽', '🟡', '🟢'];
    const leftEmoji = countingObjects[Math.floor(Math.random() * countingObjects.length)];
    const rightEmoji = countingObjects[Math.floor(Math.random() * countingObjects.length)];

    const leftItem: GameItem = { id: 'left-items', emoji: leftEmoji, value: leftCount, type: 'count' };
    const rightItem: GameItem = { id: 'right-items', emoji: rightEmoji, value: rightCount, type: 'count' };

    let correctAnswer: ComparisonResult = 'equal';
    if (leftCount > rightCount) correctAnswer = 'more';
    if (leftCount < rightCount) correctAnswer = 'less';

    return {
      mode: 'count',
      leftItem,
      rightItem,
      correctAnswer,
      instruction: 'Count carefully! Which side has more items?'
    };
  }

  function generateSizeChallenge(): Challenge {
    const sizeItems: GameItem[] = [
      // Very tiny animals
      { id: 'ant', emoji: '🐜', value: 1, type: 'size' },
      { id: 'bee', emoji: '🐝', value: 1, type: 'size' },
      { id: 'ladybug', emoji: '🐞', value: 1, type: 'size' },
      { id: 'butterfly', emoji: '🦋', value: 1, type: 'size' },
      
      // Small animals
      { id: 'mouse', emoji: '🐭', value: 3, type: 'size' },
      { id: 'hamster', emoji: '🐹', value: 3, type: 'size' },
      { id: 'rabbit', emoji: '🐰', value: 3, type: 'size' },
      { id: 'chicken', emoji: '🐔', value: 3, type: 'size' },
      { id: 'duck', emoji: '🦆', value: 3, type: 'size' },
      
      // Medium animals
      { id: 'cat', emoji: '🐱', value: 5, type: 'size' },
      { id: 'dog', emoji: '🐶', value: 5, type: 'size' },
      { id: 'pig', emoji: '🐷', value: 5, type: 'size' },
      { id: 'sheep', emoji: '🐑', value: 5, type: 'size' },
      { id: 'goat', emoji: '🐐', value: 5, type: 'size' },
      
      // Large animals
      { id: 'horse', emoji: '🐴', value: 8, type: 'size' },
      { id: 'cow', emoji: '🐄', value: 8, type: 'size' },
      { id: 'bear', emoji: '🐻', value: 8, type: 'size' },
      { id: 'lion', emoji: '🦁', value: 8, type: 'size' },
      { id: 'tiger', emoji: '🐅', value: 8, type: 'size' },
      
      // Very large animals
      { id: 'elephant', emoji: '🐘', value: 10, type: 'size' },
      { id: 'whale', emoji: '🐋', value: 10, type: 'size' },
      { id: 'giraffe', emoji: '🦒', value: 10, type: 'size' },
      { id: 'dinosaur', emoji: '🦕', value: 10, type: 'size' },
      { id: 'hippo', emoji: '🦛', value: 10, type: 'size' },
    ];

    const leftItem = sizeItems[Math.floor(Math.random() * sizeItems.length)];
    const rightItem = sizeItems[Math.floor(Math.random() * sizeItems.length)];

    let correctAnswer: ComparisonResult = 'equal';
    if (leftItem.value > rightItem.value) correctAnswer = 'more';
    if (leftItem.value < rightItem.value) correctAnswer = 'less';

    return {
      mode: 'size',
      leftItem,
      rightItem,
      correctAnswer,
      instruction: `Look at the animals! Which one is bigger - the ${leftItem.id} or the ${rightItem.id}?`
    };
  }

  const handleAnswerClick = (answer: ComparisonResult) => {
    if (selectedAnswer !== null) return;

    setSelectedAnswer(answer);
    const correct = answer === challenge.correctAnswer;
    setIsCorrect(correct);

    if (correct) {
      setScore(prevScore => prevScore + 1);
      playSound('success');
    } else {
      playSound('error');
    }

    updateProgress('comparison-champions', score + (correct ? 1 : 0));

    setTimeout(() => {
      setSelectedAnswer(null);
      setIsCorrect(null);
      setChallenge(generateChallenge());
    }, 3000);
  };

  const playSound = (type: 'success' | 'error' | 'click') => {
    console.log(`Playing ${type} sound`);
  };

  const renderComparisonArea = () => {
    if (challenge.mode === 'count') {
      return (
        <FlexContainer>
          <ComparisonZone itemCount={challenge.leftItem.value}>
            {Array.from({ length: challenge.leftItem.value }, (_, index) => (
              <span key={index}>{challenge.leftItem.emoji}</span>
            ))}
          </ComparisonZone>
          <div style={{ margin: '0 2rem', fontSize: '2rem' }}>VS</div>
          <ComparisonZone itemCount={challenge.rightItem.value}>
            {Array.from({ length: challenge.rightItem.value }, (_, index) => (
              <span key={index}>{challenge.rightItem.emoji}</span>
            ))}
          </ComparisonZone>
        </FlexContainer>
      );
    }
    
    return (
      <FlexContainer>
        <ComparisonZone>
          {challenge.leftItem.emoji}
        </ComparisonZone>
        <div style={{ margin: '0 2rem', fontSize: '2rem' }}>VS</div>
        <ComparisonZone>
          {challenge.rightItem.emoji}
        </ComparisonZone>
      </FlexContainer>
    );
  };

  const getAnswerLabels = (): { more: string; less: string; equal: string } => {
    switch (challenge.mode) {
      case 'scale':
        return { more: 'Heavier', less: 'Lighter', equal: 'Same Weight' };
      case 'count':
        return { more: 'More', less: 'Less', equal: 'Same Amount' };
      case 'size':
        return { more: 'Bigger', less: 'Smaller', equal: 'Same Size' };
      default:
        return { more: 'More', less: 'Less', equal: 'Equal' };
    }
  };

  const answerLabels = getAnswerLabels();

  return (
    <Container>
      <PageTitle>Comparison Champions</PageTitle>

      <GameContainer>
        <FlexContainer justify="space-between">
          <Link to="/">
            <Button variant="secondary">Back to Games</Button>
          </Link>

          <ScoreDisplay>
            Score: <span>{score}</span>
          </ScoreDisplay>
        </FlexContainer>

        <InstructionText>
          {challenge.instruction}
        </InstructionText>

        <GameArea>
          <ComparisonArea>
            {renderComparisonArea()}
          </ComparisonArea>

          <AnswerArea>
            <h3>Choose Answer:</h3>
            <AnswerButtonContainer>
              <AnswerButton
                isSelected={selectedAnswer === 'more'}
                isCorrect={isCorrect ?? undefined}
                onClick={() => handleAnswerClick('more')}
              >
                {answerLabels.more}
              </AnswerButton>
              <AnswerButton
                isSelected={selectedAnswer === 'less'}
                isCorrect={isCorrect ?? undefined}
                onClick={() => handleAnswerClick('less')}
              >
                {answerLabels.less}
              </AnswerButton>
              <AnswerButton
                isSelected={selectedAnswer === 'equal'}
                isCorrect={isCorrect ?? undefined}
                onClick={() => handleAnswerClick('equal')}
              >
                {answerLabels.equal}
              </AnswerButton>
            </AnswerButtonContainer>
          </AnswerArea>
        </GameArea>

        {isCorrect !== null && (
          <FeedbackMessage isCorrect={isCorrect}>
            {isCorrect
              ? '🎉 Fantastic! You got it right! 🎉'
              : `Good try! The correct answer was "${answerLabels[challenge.correctAnswer]}".`}
          </FeedbackMessage>
        )}
      </GameContainer>
    </Container>
  );
};

export default ComparisonChampions;